import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Table, Tag, Button, Card, Select, Space, Typography,
  Popover, Tooltip, message, Spin, Alert, Statistic, Row, Col, Progress, Input,
} from 'antd';
import {
  SyncOutlined, DownloadOutlined, FilePdfOutlined,
  ExclamationCircleOutlined, CheckCircleOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';

import { getAgentAuthHeaders } from '../../services/api/settingsApi';

const { Title, Text } = Typography;
const AGENT_URL = import.meta.env.VITE_CSG_GATEWAY_URL || 'http://localhost:3000';

// ── API helpers ──────────────────────────────────────────────────────────────

async function fetchMfaResults(classification, severity) {
  const params = new URLSearchParams();
  if (classification && classification !== 'ALL') params.set('classification', classification);
  if (severity && severity !== 'ALL') params.set('severity', severity);
  const qs = params.toString();
  const url = `${AGENT_URL}/mfa-scan/results${qs ? `?${qs}` : ''}`;

  const response = await fetch(url, { headers: getAgentAuthHeaders() });
  if (!response.ok) {
    const err = new Error(`MFA scan results request failed (${response.status})`);
    err.response = { status: response.status, data: await response.json().catch(() => ({})) };
    throw err;
  }
  return response.json();
}

async function triggerMfaScan() {
  const response = await fetch(`${AGENT_URL}/mfa-scan/trigger`, {
    method: 'POST',
    headers: getAgentAuthHeaders(),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err = new Error(`Trigger MFA scan failed (${response.status})`);
    err.response = { status: response.status, data };
    throw err;
  }
  return response.json();
}

async function fetchMfaActiveRun() {
  const response = await fetch(`${AGENT_URL}/mfa-scan/runs/active`, {
    headers: getAgentAuthHeaders(),
  });
  if (response.status === 404) return null;
  if (!response.ok) return null;
  return response.json();
}

// ── Visual config ────────────────────────────────────────────────────────────

const CLASSIFICATION_PALETTE = {
  SECURITY_DEFAULTS:  { color: 'var(--odin-status-completed-text)', label: 'SECURITY DEFAULTS' },
  CONDITIONAL_ACCESS: { color: 'var(--odin-accent)',                label: 'CONDITIONAL ACCESS' },
  WEAK_MFA:           { color: 'var(--odin-status-awaiting-text)',  label: 'WEAK MFA' },       // was LEGACY_MFA / LEGACY MFA
  NO_MFA:             { color: 'var(--odin-status-failed-text)',    label: 'NO MFA' },
  CONFLICT:           { color: 'var(--odin-status-active-text)',    label: 'CONFLICT' },
  DATA_ERROR:         { color: 'var(--odin-text-secondary)',        label: 'DATA ERROR' },
};

const SEVERITY_STYLE = {
  HIGH:   { background: 'var(--odin-status-failed-bg)',   color: 'var(--odin-status-failed-text)',   border: '1px solid var(--odin-status-failed-border)' },
  MEDIUM: { background: 'var(--odin-status-awaiting-bg)', color: 'var(--odin-status-awaiting-text)', border: '1px solid var(--odin-status-awaiting-border)' },
  INFO:   { background: 'var(--odin-status-active-bg)',   color: 'var(--odin-status-active-text)',   border: '1px solid var(--odin-status-active-border)' },
};

const EXCEPTION_RECOMMENDATIONS = {
  CONFLICT_SD_AND_CA: 'Disable Security Defaults — Microsoft expects only one enforcement model at a time.',
  NO_ADMIN_MFA: 'Add an admin-targeted MFA policy (Global Administrator role at minimum).',
  ADMIN_EXCLUDED: 'Remove the admin role from the policy excludeRoles list.',
  REPORT_ONLY_POLICY: 'Promote the policy from report-only to enabled, after reviewing impact in sign-in logs.',
  BROAD_LOCATION_EXCLUSION: 'Replace the broad named location with a narrowly-scoped one.',
  PER_USER_MFA_LEGACY:
    'Microsoft deprecated per-user MFA on 2024-09-30. Migrate the affected users to CA-driven MFA — ' +
    'use Update-EntraBetaUserAuthenticationRequirement -PerUserMfaState disabled, or remove their ' +
    'StrongAuthenticationRequirements via the legacy MSOnline path if available.',
  BREAK_GLASS_NOTED: 'Documented exclusion — verify the account is still required and rotation-tested.',
};

function ClassificationBadge({ value }) {
  const cfg = CLASSIFICATION_PALETTE[value] || CLASSIFICATION_PALETTE.DATA_ERROR;
  return (
    <Tag style={{
      background: `color-mix(in srgb, ${cfg.color} 16%, transparent)`, color: cfg.color,
      border: `1px solid color-mix(in srgb, ${cfg.color} 40%, transparent)`,
      fontWeight: 700, fontSize: 10, letterSpacing: '0.04em',
    }}>
      {cfg.label}
    </Tag>
  );
}

// asyncpg returns JSONB columns as JSON-encoded strings on the wire (matches
// the CE scan widget's parseArray helper). Defensively coerce so JSX consumers
// don't crash when JSON.stringify-roundtripped arrays come back as strings.
function parseArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch { return []; }
  }
  return [];
}

function ExceptionsCell({ exceptions }) {
  if (!exceptions || exceptions.length === 0) return <Text type="secondary">None</Text>;
  return (
    <Space size={4} wrap>
      {exceptions.map((e, i) => {
        const affected = parseArray(e.affected_entities);
        return (
        <Popover
          key={`${e.type}-${i}`}
          title={`${e.severity} · ${e.type}`}
          content={(
            <div style={{ maxWidth: 320 }}>
              <div style={{ marginBottom: 8 }}>{e.description}</div>
              {e.policy_name && <div><b>Policy:</b> {e.policy_name}</div>}
              {affected.length > 0 && (
                <div>
                  <b>Affected ({affected.length}):</b>{' '}
                  {affected.slice(0, 8).join(', ')}
                  {affected.length > 8 && (
                    <Text type="secondary"> + {affected.length - 8} more</Text>
                  )}
                </div>
              )}
              <div style={{ marginTop: 8, color: 'var(--odin-accent)' }}>
                <b>Recommendation:</b> {EXCEPTION_RECOMMENDATIONS[e.type] || '(no recommendation)'}
              </div>
            </div>
          )}
        >
          <span style={{
            ...SEVERITY_STYLE[e.severity],
            display: 'inline-block', padding: '1px 6px', borderRadius: 8,
            fontSize: 10, fontWeight: 600, cursor: 'pointer',
          }}>
            {e.severity[0]} · {e.type.replace(/_/g, ' ').toLowerCase()}
          </span>
        </Popover>
        );
      })}
    </Space>
  );
}

function Bool({ value }) {
  if (value === null || value === undefined) return <Text type="secondary">—</Text>;
  return value
    ? <CheckCircleOutlined style={{ color: 'var(--odin-success)' }} />
    : <ExclamationCircleOutlined style={{ color: 'var(--odin-error)' }} />;
}

const COOLDOWN_MS = 30 * 60 * 1000;
function isCoolingDown(lastScanAt) {
  if (!lastScanAt) return false;
  return Date.now() - new Date(lastScanAt).getTime() < COOLDOWN_MS;
}

// ── Main component ───────────────────────────────────────────────────────────

export default function MfaClassificationReport() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('Admin');
  const [classificationFilter, setClassificationFilter] = useState('ALL');
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [highOnly, setHighOnly] = useState(false);
  const [search, setSearch] = useState('');

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['mfa-scan-results', classificationFilter, highOnly ? 'HIGH' : severityFilter],
    queryFn: () => fetchMfaResults(classificationFilter, highOnly ? 'HIGH' : severityFilter),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: activeRun, refetch: refetchActive } = useQuery({
    queryKey: ['mfa-scan-active'],
    queryFn: fetchMfaActiveRun,
    refetchInterval: (q) => (q.state.data?.id ? 5000 : 60000),
    staleTime: 0,
    initialData: null,
  });

  const prevActiveId = useRef(undefined);
  useEffect(() => {
    if (prevActiveId.current !== undefined && prevActiveId.current !== null && !activeRun?.id) {
      refetch();
      message.success('MFA scan complete — results updated.');
    }
    prevActiveId.current = activeRun?.id ?? null;
  }, [activeRun?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerMutation = useMutation({
    mutationFn: triggerMfaScan,
    onSuccess: () => setTimeout(refetchActive, 1500),
    onError: (err) => {
      if (err.response?.status === 429) {
        const secs = err.response.data?.detail?.retryAfterSeconds ?? 1800;
        message.warning(`Scan triggered recently — wait ${Math.ceil(secs / 60)} more minutes.`);
      } else {
        message.error('Failed to trigger MFA scan.');
      }
    },
  });

  const scanRun = data?.scan_run;
  const cooling = isCoolingDown(scanRun?.started_at);
  const isScanning = !!activeRun?.id;
  const scanPct = activeRun?.total_expected > 0
    ? Math.round((activeRun.scanned_so_far / activeRun.total_expected) * 100) : 0;

  const filteredResults = (data?.results || []).filter(r =>
    !search || (r.tenant_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      title: 'Customer',
      dataIndex: 'tenant_name',
      sorter: (a, b) => (a.tenant_name || '').localeCompare(b.tenant_name || ''),
      render: (name, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {row.default_domain && <Text type="secondary" style={{ fontSize: 10 }}>{row.default_domain}</Text>}
        </Space>
      ),
    },
    {
      title: 'Classification',
      dataIndex: 'classification',
      sorter: (a, b) => a.classification.localeCompare(b.classification),
      render: (v) => <ClassificationBadge value={v} />,
    },
    { title: 'SD', dataIndex: 'security_defaults_enabled', render: (v) => <Bool value={v} />, align: 'center' },
    {
      title: 'CA enabled / total',
      key: 'ca',
      align: 'center',
      render: (_, r) => `${r.ca_enabled_count || 0} / ${r.ca_total_count || 0}`,
    },
    { title: 'MFA via CA', dataIndex: 'mfa_policy_exists', render: (v) => <Bool value={v} />, align: 'center' },
    { title: 'Admin MFA', dataIndex: 'admin_mfa_policy_exists', render: (v) => <Bool value={v} />, align: 'center' },
    {
      title: 'Per-user MFA users',
      dataIndex: 'per_user_mfa_user_count',
      align: 'center',
      render: (v) => v ? <Tag color="warning">{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Exceptions',
      dataIndex: 'exceptions',
      render: (excs) => <ExceptionsCell exceptions={excs} />,
    },
  ];

  async function downloadFile(url, filename) {
    try {
      const response = await fetch(url, { headers: getAgentAuthHeaders() });
      if (!response.ok) {
        message.error(`Export failed (${response.status}).`);
        return;
      }
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href; link.download = filename;
      document.body.appendChild(link); link.click();
      document.body.removeChild(link); URL.revokeObjectURL(href);
    } catch {
      message.error('Export failed.');
    }
  }
  const handleDownloadCsv = () => downloadFile(`${AGENT_URL}/mfa-scan/export/csv`, 'mfa-classification.csv');
  const handleDownloadPdf = () => downloadFile(`${AGENT_URL}/mfa-scan/export/pdf`, 'mfa-classification.pdf');

  if (isPending) return <Spin tip="Loading MFA classification data..." style={{ display: 'block', margin: '40px auto' }} />;

  if (isError) {
    const is404 = error?.response?.status === 404;
    return (
      <div style={{ padding: '24px' }}>
        {isScanning && (
          <Alert
            type="info" style={{ marginBottom: 16 }}
            message={
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Space><SyncOutlined spin /><Text strong>MFA scan in progress</Text>
                  {activeRun?.total_expected > 0 && (
                    <Text type="secondary">{activeRun.scanned_so_far} / {activeRun.total_expected} tenants</Text>
                  )}
                </Space>
                <Progress percent={scanPct} status="active" size="small" strokeColor="var(--odin-accent)"
                  showInfo={activeRun?.total_expected > 0} />
              </Space>
            }
          />
        )}
        <Alert
          type={is404 ? 'info' : 'error'}
          message={is404 ? 'No MFA scans yet' : 'Failed to load MFA classification data'}
          description={
            is404
              ? isAdmin
                ? 'No completed scans found. Click "Refresh Now" to run the first scan.'
                : 'No completed scans found. An administrator can trigger a manual scan, or wait for the next scheduled scan (Mon-Fri 19:00 Europe/London).'
              : error?.message
          }
          action={isAdmin && (
            <Button type="primary" loading={triggerMutation.isPending} onClick={() => triggerMutation.mutate()}>
              Refresh Now
            </Button>
          )}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={4} style={{ margin: 0 }}>M365 Security / MFA Classification</Title>
          {scanRun && (
            <Text type="secondary">
              Last scan completed: {new Date(scanRun.completed_at || scanRun.started_at).toLocaleString()}
              {' · '}{scanRun.tenant_count || 0} tenants
            </Text>
          )}
        </div>
        {isAdmin && (
          <Tooltip title={cooling ? 'Scan triggered recently — please wait' : 'Run scan now'}>
            <Button icon={<SyncOutlined />} loading={triggerMutation.isPending}
              disabled={cooling} onClick={() => triggerMutation.mutate()}>
              Refresh Now
            </Button>
          </Tooltip>
        )}
      </Space>

      {isScanning && (
        <Alert
          type="info" style={{ marginBottom: 16 }}
          message={
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <Space><SyncOutlined spin /><Text strong>MFA scan in progress</Text>
                {activeRun?.total_expected > 0 && (
                  <Text type="secondary">{activeRun.scanned_so_far} / {activeRun.total_expected} tenants</Text>
                )}
              </Space>
              <Progress percent={scanPct} status="active" size="small" strokeColor="var(--odin-accent)"
                showInfo={activeRun.total_expected > 0} />
            </Space>
          }
        />
      )}

      {scanRun && (
        <Row gutter={12} style={{ marginBottom: 16 }}>
          {[
            { key: 'security_defaults_count',  label: 'Security Defaults', color: CLASSIFICATION_PALETTE.SECURITY_DEFAULTS.color },
            { key: 'conditional_access_count', label: 'Conditional Access', color: CLASSIFICATION_PALETTE.CONDITIONAL_ACCESS.color },
            { key: 'weak_mfa_count',           label: 'Weak MFA',           color: CLASSIFICATION_PALETTE.WEAK_MFA.color },
            { key: 'no_mfa_count',             label: 'No MFA',             color: CLASSIFICATION_PALETTE.NO_MFA.color },
            { key: 'conflict_count',           label: 'Conflict',           color: CLASSIFICATION_PALETTE.CONFLICT.color },
            { key: 'data_error_count',         label: 'Data Error',         color: CLASSIFICATION_PALETTE.DATA_ERROR.color },
          ].map(({ key, label, color }) => (
            <Col span={4} key={key}>
              <Card size="small">
                <Statistic title={label} value={scanRun[key] ?? 0} valueStyle={{ color }} />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Space style={{ marginBottom: 12 }} wrap>
        <Select value={classificationFilter} onChange={setClassificationFilter} style={{ width: 200 }}
          options={[
            { value: 'ALL', label: 'All Classifications' },
            { value: 'SECURITY_DEFAULTS', label: 'Security Defaults' },
            { value: 'CONDITIONAL_ACCESS', label: 'Conditional Access' },
            { value: 'WEAK_MFA', label: 'Weak MFA' },
            { value: 'NO_MFA', label: 'No MFA' },
            { value: 'CONFLICT', label: 'Conflict' },
            { value: 'DATA_ERROR', label: 'Data Error' },
          ]} />
        <Button type={highOnly ? 'primary' : 'default'} onClick={() => setHighOnly(v => !v)}>
          {highOnly ? 'HIGH only (on)' : 'HIGH exceptions only'}
        </Button>
        <Input prefix={<SearchOutlined />} placeholder="Search customer..." value={search}
          onChange={e => setSearch(e.target.value)} style={{ width: 220 }} allowClear />
        <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv}>Export CSV</Button>
        <Button icon={<FilePdfOutlined />} onClick={handleDownloadPdf}>Export PDF</Button>
      </Space>

      <Table
        dataSource={filteredResults}
        columns={columns}
        rowKey={(r) => r.id || r.tenant_id || String(Math.random())}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true }}
        expandable={{
          expandedRowRender: (row) => {
            const policies = parseArray(row.raw_policies);
            const upns = parseArray(row.per_user_mfa_users);
            return (
              <div style={{ padding: 8 }}>
                <Text strong>Conditional Access policies ({row.ca_total_count || 0}):</Text>
                <pre style={{ fontSize: 11, background: 'var(--odin-code-bg)', padding: 8, marginTop: 4,
                              maxHeight: 240, overflow: 'auto', borderRadius: 4 }}>
                  {policies.length > 0 ? JSON.stringify(policies, null, 2) : '(no policies)'}
                </pre>
                {upns.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <Text strong>Users with per-user MFA active ({upns.length}):</Text>
                    <pre style={{ fontSize: 11, background: 'var(--odin-code-bg)', padding: 8, marginTop: 4,
                                  maxHeight: 160, overflow: 'auto', borderRadius: 4 }}>
                      {upns.join('\n')}
                    </pre>
                  </div>
                )}
              </div>
            );
          },
          rowExpandable: (row) =>
            parseArray(row.raw_policies).length > 0 ||
            parseArray(row.per_user_mfa_users).length > 0,
        }}
      />
    </div>
  );
}
